import {
  Timestamp,
  collection,
  doc,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  where,
  writeBatch,
} from "firebase/firestore";
import { auth, db } from "./firebase";
import {
  BUSINESS_NETWORK_EVENT_SERIES,
  businessNetworkEventStartDate,
  buildBusinessNetworkEventRecord,
  canonicalBusinessNetworkEventId,
  normaliseBusinessNetworkEvent,
  normaliseBusinessNetworkRegistration,
} from "./businessNetworkAdminUtils";

const REGISTRATION_STATUSES = new Set(["confirmed", "cancelled"]);
const ATTENDANCE_STATUSES = new Set(["registered", "attended", "no_show"]);

export function subscribeBusinessNetworkEvents(onData, onError) {
  const eventQuery = query(
    collection(db, "events"),
    where("eventSeries", "==", BUSINESS_NETWORK_EVENT_SERIES),
  );
  return onSnapshot(
    eventQuery,
    (snapshot) => onData(
      snapshot.docs
        .map((item) => normaliseBusinessNetworkEvent(item.id, item.data()))
        .filter((event) => event.status === "approved"),
    ),
    onError,
  );
}

export function subscribeBusinessNetworkRegistrations(onData, onError) {
  return onSnapshot(
    collection(db, "businessNetworkLaunchRegistrations"),
    (snapshot) => onData(snapshot.docs.map((item) => normaliseBusinessNetworkRegistration(item.id, item.data()))),
    onError,
  );
}

function requireAdminUid() {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("Administrator authentication is required.");
  return uid;
}

export async function updateBusinessNetworkRegistration(registration, changes) {
  if (!registration?.id) throw new Error("A registration is required.");
  const uid = requireAdminUid();
  const allowedChanges = {};
  if (
    REGISTRATION_STATUSES.has(changes.registrationStatus)
    && changes.registrationStatus !== registration.registrationStatus
  ) {
    allowedChanges.registrationStatus = changes.registrationStatus;
  }
  if (
    ATTENDANCE_STATUSES.has(changes.attendanceStatus)
    && changes.attendanceStatus !== registration.attendanceStatus
  ) {
    allowedChanges.attendanceStatus = changes.attendanceStatus;
  }
  if (!Object.keys(allowedChanges).length) throw new Error("No valid registration changes were supplied.");

  const batch = writeBatch(db);
  const timestamp = serverTimestamp();
  batch.update(doc(db, "businessNetworkLaunchRegistrations", registration.id), {
    ...allowedChanges,
    updatedAt: timestamp,
    updatedBy: uid,
  });

  Object.entries(allowedChanges).forEach(([field, newValue]) => {
    const auditRef = doc(collection(db, "businessNetworkRegistrationAuditLog"));
    batch.set(auditRef, {
      registrationId: registration.registrationId || registration.id,
      eventId: canonicalBusinessNetworkEventId(registration.eventId),
      action: `${field}_updated`,
      previousValue: String(registration[field] || ""),
      newValue: String(newValue),
      createdAt: timestamp,
      createdBy: uid,
    });
  });

  await batch.commit();
}

export async function createBusinessNetworkEvent(values) {
  requireAdminUid();
  const event = buildBusinessNetworkEventRecord(values);
  const eventReference = doc(db, "events", event.eventId);
  const eventDate = Timestamp.fromDate(
    businessNetworkEventStartDate(event.date, event.startTime),
  );

  await runTransaction(db, async (transaction) => {
    const existing = await transaction.get(eventReference);
    if (existing.exists()) {
      throw new Error("A Business Network event already exists for that date.");
    }
    const timestamp = serverTimestamp();
    transaction.set(eventReference, {
      ...event,
      eventDate,
      createdAt: timestamp,
      updatedAt: timestamp,
    });
  });

  return { ...event, eventDate };
}
